(function () {
  'use strict';

  // ---------- CONFIG ----------
  // Fit fields per category.
  // Each entry: { label, fields: [{ key, label, type, options? }] }
  // Apparel categories (tops/bottoms/dresses/outerwear) also carry:
  //   height: true          → required Height field
  //   fitPreference: true   → required Oversized/True to fit/Fitted select
  //   usualSize: {...}       → optional size input (alternative to measurements)
  //   cup: true             → optional cup/band size field
  // `fields` is measurements ONLY. Per apparel item, a user must give either a
  // usual size OR at least one measurement (see validateFit).
  const FIT_PREF_OPTIONS = ['Oversized', 'True to fit', 'Fitted'];
  const LETTER_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];
  const WAIST_SIZES = ['22', '23', '24', '25', '26', '27', '28', '29', '30', '31', '32', '33', '34', '35', '36', '37', '38', '40', '42', '44'];
  const BAND_SIZES = ['30', '32', '34', '36', '38', '40', '42', '44'];
  const CUP_SIZES = ['A', 'B', 'C', 'D', 'DD', 'DDD'];
  const SHOE_SIZES = ['4', '4.5', '5', '5.5', '6', '6.5', '7', '7.5', '8', '8.5', '9', '9.5', '10', '10.5', '11', '11.5', '12', '12.5', '13', '13.5', '14', '14.5', '15'];

  // Step 2 "Tell us what you're hunting for" placeholder, per category.
  const INSPO_PLACEHOLDERS = {
    tops: 'cropped ribbed tank, worn-in band tee, 90s feel',
    bottoms: 'high-waisted Levi’s, faded wide-leg, baggy carpenter',
    dresses: 'floral slip dress, 70s prairie, silky midi',
    outerwear: 'lived-in black moto jacket, oversized, 90s feel',
    shoes: 'chunky black loafers, worn-in cowboy boots, retro sneakers',
    accessories: 'woven leather belt, gold hoops, slouchy shoulder bag'
  };
  const INSPO_PLACEHOLDER_DEFAULT = 'describe the piece — style, era, color, vibe';

  const FIT_CONFIG = {
    tops: {
      label: 'Tops',
      height: true,
      fitPreference: true,
      usualSize: { type: 'select', label: 'Usual size', options: LETTER_SIZES },
      cup: true,
      fields: [
        { key: 'bust', label: 'Bust', type: 'text', placeholder: 'e.g. 34 in' },
        { key: 'shoulder', label: 'Shoulder to Shoulder', type: 'text', placeholder: 'e.g. 16 in' },
        { key: 'armLength', label: 'Arm Length', type: 'text', placeholder: 'e.g. 24 in' },
        { key: 'torsoLength', label: 'Torso Length', type: 'text', placeholder: 'under breast to underwear line (in)' }
      ]
    },
    bottoms: {
      label: 'Bottoms',
      height: true,
      fitPreference: true,
      usualSize: { type: 'select', label: 'Usual waist size', options: WAIST_SIZES },
      fields: [
        { key: 'waist', label: 'Waist', type: 'text', placeholder: 'around the natural waist (in)' },
        { key: 'lowWaist', label: 'Low waist', type: 'text', placeholder: 'where low-rise pants sit (in)' },
        { key: 'inseam', label: 'Inseam', type: 'text', placeholder: 'crotch to ankle (in)' },
        { key: 'outseam', label: 'Outseam', type: 'text', placeholder: 'waist to ankle along outside leg (in)' },
        { key: 'rise', label: 'Rise', type: 'text', placeholder: 'crotch to waistband (in)' }
      ]
    },
    dresses: {
      label: 'Dresses',
      height: true,
      fitPreference: true,
      usualSize: { type: 'select', label: 'Usual size', options: LETTER_SIZES },
      cup: true,
      fields: [
        { key: 'armLength', label: 'Arm Length', type: 'text', placeholder: 'e.g. 24 in' },
        { key: 'bust', label: 'Bust', type: 'text', placeholder: 'e.g. 34 in' },
        { key: 'waist', label: 'Waist', type: 'text', placeholder: 'e.g. 28 in' }
      ]
    },
    outerwear: {
      label: 'Outerwear',
      height: true,
      fitPreference: true,
      usualSize: { type: 'select', label: 'Usual size', options: LETTER_SIZES },
      fields: [
        { key: 'armLength', label: 'Arm Length', type: 'text', placeholder: 'e.g. 24 in' },
        { key: 'bust', label: 'Bust', type: 'text', placeholder: 'e.g. 34 in' },
        { key: 'waist', label: 'Waist', type: 'text', placeholder: 'e.g. 28 in' },
        { key: 'shoulder', label: 'Shoulder to Shoulder', type: 'text', placeholder: 'e.g. 16 in' }
      ]
    },
    shoes: {
      label: 'Shoes',
      fields: [
        { key: 'shoeGender', label: 'Men\u2019s or Women\u2019s', type: 'radio', options: ['Women\u2019s', 'Men\u2019s'], required: true },
        { key: 'size', label: 'American Shoe Size', type: 'select', options: SHOE_SIZES, placeholder: 'Select size', required: true }
      ]
    },
    accessories: {
      label: 'Accessories',
      // Accessories don't use FIT_CONFIG.fields — type is picked in step 2 (per item)
      // and step 3 renders measurements via ACCESSORY_MEASUREMENTS keyed by type.
      fields: []
    }
  };

  // Accessory type select shown on step 2 for each accessory item.
  const ACCESSORY_TYPES = ['Handbag', 'Belt'];
  const ACCESSORY_MEASUREMENTS = {
    Belt: [
      { key: 'beltWaist', label: 'Waist', placeholder: 'around the natural waist (in)' },
      { key: 'beltLowWaist', label: 'Low waist', placeholder: 'where low-rise pants sit (in)' }
    ]
    // Handbag and Other: no measurement fields
  };

  const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
  const MAX_VIDEO_BYTES = 10 * 1024 * 1024;
  const TOTAL_STEPS = 6;
  const SUBMIT_ENDPOINT = 'https://thriftknd-vintage-request.brittany-f46.workers.dev/';

  // ---------- STATE ----------
  // state.inspo is keyed by item key `${cat}_${index}` → { text, files: [] }
  const state = {
    step: 1,
    categories: [],
    quantities: {},
    inspo: {},
    fit: {},
    bodyVideo: null,
    info: { name: '', email: '', phone: '', instagram: '' },
    reviewFitTab: null
  };

  function inspoKey(cat, i) { return cat + '_' + i; }

  function inspoItems() {
    const items = [];
    state.categories.forEach((cat) => {
      const qty = state.quantities[cat] || 1;
      const label = (FIT_CONFIG[cat] && FIT_CONFIG[cat].label) || cat;
      for (let i = 0; i < qty; i++) {
        items.push({
          cat,
          index: i,
          key: inspoKey(cat, i),
          label: qty > 1 ? label + ' #' + (i + 1) : label
        });
      }
    });
    return items;
  }

  function ensureInspoBucket(key) {
    if (!state.inspo[key]) state.inspo[key] = { text: '', files: [], pinterest: [] };
    return state.inspo[key];
  }

  // Restrict input to digits, decimal point, apostrophe, and double quote
  // (so users can type "28", "8.5", or 5'7" for height — but no letters).
  const NUMERIC_ALLOWED = /[^0-9.'"]/g;
  function wireNumericInput(input) {
    input.inputMode = 'decimal';
    input.autocomplete = 'off';
    input.addEventListener('input', (e) => {
      const cleaned = e.target.value.replace(NUMERIC_ALLOWED, '');
      if (cleaned !== e.target.value) e.target.value = cleaned;
    });
  }

  // Step 4 (body video) is skipped when only accessories and/or shoes are picked.
  // Step 3 (measurements) is skipped when no selected categories need measurements
  // (e.g., only accessories with all Handbag/Other types).
  const NO_BODY_CATEGORIES = ['accessories', 'shoes'];
  function visibleSteps() {
    const all = [1, 2, 3, 4, 5, 6];
    let steps = all;
    if (state.categories.length > 0 && state.categories.every((c) => NO_BODY_CATEGORIES.indexOf(c) !== -1)) {
      steps = steps.filter((s) => s !== 4);
    }
    if (state.categories.length > 0 && !state.categories.some(categoryNeedsFitStep)) {
      steps = steps.filter((s) => s !== 3);
    }
    return steps;
  }

  // ---------- CUSTOM SELECT ----------
  function createCustomSelect(opts) {
    const options = opts.options || [];
    const placeholder = opts.placeholder || 'Select';
    let value = opts.value || '';
    let activeIndex = options.indexOf(value);

    const root = document.createElement('div');
    root.className = 'vr-select';

    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'vr-select__trigger';
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');

    const valueSpan = document.createElement('span');
    valueSpan.className = 'vr-select__value';
    if (value) valueSpan.textContent = value;
    else { valueSpan.textContent = placeholder; valueSpan.classList.add('vr-select__value--placeholder'); }
    trigger.appendChild(valueSpan);
    trigger.insertAdjacentHTML('beforeend',
      '<svg class="vr-select__chevron" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>'
    );

    const menu = document.createElement('ul');
    menu.className = 'vr-select__menu';
    menu.setAttribute('role', 'listbox');
    menu.hidden = true;

    options.forEach((opt, i) => {
      const li = document.createElement('li');
      li.className = 'vr-select__option';
      li.setAttribute('role', 'option');
      li.dataset.value = opt;
      li.textContent = opt;
      if (opt === value) li.setAttribute('aria-selected', 'true');
      li.addEventListener('mousedown', (e) => { e.preventDefault(); choose(opt); });
      li.addEventListener('mouseenter', () => setActive(i));
      menu.appendChild(li);
    });

    function setActive(i) {
      activeIndex = i;
      menu.querySelectorAll('.vr-select__option').forEach((el, idx) => {
        el.classList.toggle('is-active', idx === i);
      });
    }
    function open() {
      if (!menu.hidden) return;
      menu.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      root.classList.add('is-open');
      if (activeIndex >= 0) setActive(activeIndex);
      document.addEventListener('mousedown', onDocClick, true);
      document.addEventListener('keydown', onKey, true);
    }
    function close() {
      if (menu.hidden) return;
      menu.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
      root.classList.remove('is-open');
      document.removeEventListener('mousedown', onDocClick, true);
      document.removeEventListener('keydown', onKey, true);
    }
    function choose(opt) {
      value = opt;
      valueSpan.textContent = opt;
      valueSpan.classList.remove('vr-select__value--placeholder');
      menu.querySelectorAll('.vr-select__option').forEach((el) => {
        el.setAttribute('aria-selected', el.dataset.value === opt ? 'true' : 'false');
      });
      close();
      trigger.focus();
      if (opts.onChange) opts.onChange(opt);
    }
    function onDocClick(e) { if (!root.contains(e.target)) close(); }
    function onKey(e) {
      if (e.key === 'Escape') { e.preventDefault(); close(); trigger.focus(); }
      else if (e.key === 'ArrowDown') { e.preventDefault(); setActive(Math.min(options.length - 1, activeIndex + 1)); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(Math.max(0, activeIndex - 1)); }
      else if (e.key === 'Enter' || e.key === ' ') {
        if (activeIndex >= 0) { e.preventDefault(); choose(options[activeIndex]); }
      } else if (e.key === 'Tab') { close(); }
    }

    trigger.addEventListener('click', () => { if (menu.hidden) open(); else close(); });
    trigger.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
        if (menu.hidden) { e.preventDefault(); open(); }
      }
    });

    root.appendChild(trigger);
    root.appendChild(menu);
    return root;
  }

  let nextFileId = 1;
  const newId = () => 'f' + (nextFileId++);

  // ---------- BOOT ----------
  document.addEventListener('DOMContentLoaded', init);

  function init() {
    const root = document.querySelector('[data-vintage-request]');
    const modal = document.querySelector('[data-vr-modal]');
    if (!root || !modal) return;

    const form = modal.querySelector('[data-vr-form]');

    bindOpenClose(root, modal);
    bindCategories();
    bindInspo();
    bindBody();
    bindInfoInputs();
    bindNav(form);
    bindDropZones();

    render();

    sizeHero();
    window.addEventListener('resize', sizeHero);
    // Re-measure once fonts/images settle (banner height can shift).
    window.addEventListener('load', sizeHero);
  }

  // Size the hero videos so the pink banner lands at the bottom of the viewport.
  // Measures the hero's real offset from the top of the page, which already
  // accounts for whatever sits above it in normal flow — the promotion/announcement
  // bar and the site header. Desktop only; the hero is hidden on mobile.
  function sizeHero() {
    const hero = document.querySelector('.vr-hero');
    const banner = document.querySelector('.vr-banner');
    if (!hero || !banner) return;
    if (window.matchMedia('(max-width: 768px)').matches) { hero.style.height = ''; return; }
    const heroTop = hero.getBoundingClientRect().top + window.scrollY;
    const gap = parseInt(getComputedStyle(hero).marginBottom, 10) || 0;
    const available = window.innerHeight - heroTop - gap - banner.offsetHeight;
    hero.style.height = Math.max(available, 320) + 'px';
  }

  // ---------- OPEN / CLOSE ----------
  function bindOpenClose(root, modal) {
    root.querySelectorAll('[data-vr-open]').forEach((el) => {
      el.addEventListener('click', () => openModal(modal));
    });
    modal.querySelectorAll('[data-vr-close]').forEach((el) => {
      el.addEventListener('click', () => closeModal(modal));
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !modal.hasAttribute('hidden')) closeModal(modal);
    });
  }
  function openModal(modal) {
    modal.removeAttribute('hidden');
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    const first = modal.querySelector('[data-vr-category]');
    if (first) first.focus();
  }
  function closeModal(modal) {
    modal.setAttribute('hidden', '');
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  // ---------- STEP 1: CATEGORIES ----------
  function bindCategories() {
    document.querySelectorAll('[data-vr-category]').forEach((tile) => {
      const value = tile.dataset.value;

      tile.addEventListener('click', (e) => {
        if (e.target.closest('[data-vr-tile-dec], [data-vr-tile-inc]')) return;
        toggleCategory(value, tile);
      });
      tile.addEventListener('keydown', (e) => {
        if ((e.key === 'Enter' || e.key === ' ') && e.target === tile) {
          e.preventDefault();
          toggleCategory(value, tile);
        }
      });

      const dec = tile.querySelector('[data-vr-tile-dec]');
      const inc = tile.querySelector('[data-vr-tile-inc]');
      if (dec) dec.addEventListener('click', (e) => { e.stopPropagation(); decCategory(value, tile); });
      if (inc) inc.addEventListener('click', (e) => { e.stopPropagation(); incCategory(value, tile); });
    });
  }

  function toggleCategory(value, tile) {
    const idx = state.categories.indexOf(value);
    if (idx === -1) {
      state.categories.push(value);
      state.quantities[value] = 1;
      tile.setAttribute('aria-checked', 'true');
    } else {
      state.categories.splice(idx, 1);
      delete state.quantities[value];
      tile.setAttribute('aria-checked', 'false');
    }
    setTileCount(tile, state.quantities[value] || 1);
    renderFooter();
  }

  function incCategory(value, tile) {
    if (!state.quantities[value]) return;
    state.quantities[value]++;
    setTileCount(tile, state.quantities[value]);
  }

  function decCategory(value, tile) {
    if (!state.quantities[value]) return;
    if (state.quantities[value] === 1) {
      const idx = state.categories.indexOf(value);
      if (idx !== -1) state.categories.splice(idx, 1);
      delete state.quantities[value];
      tile.setAttribute('aria-checked', 'false');
      setTileCount(tile, 1);
      renderFooter();
    } else {
      state.quantities[value]--;
      setTileCount(tile, state.quantities[value]);
    }
  }

  function setTileCount(tile, qty) {
    const badge = tile.querySelector('[data-vr-tile-badge]');
    const count = tile.querySelector('[data-vr-tile-count]');
    if (badge) badge.textContent = qty;
    if (count) count.textContent = qty;
  }

  // ---------- STEP 2: INSPO ----------
  // Inputs and drop zones are bound at render time inside renderInspoStep,
  // because tabs/panels are generated dynamically per item.
  function bindInspo() {}

  // ---------- STEP 4: BODY VIDEO ----------
  function bindBody() {
    // Wire the "Record video now" button → triggers the capture-enabled file input.
    document.querySelectorAll('[data-vr-record]').forEach((btn) => {
      const which = btn.dataset.vrRecord;
      const input = document.querySelector('[data-vr-record-input="' + which + '"]');
      if (!input) return;
      btn.addEventListener('click', () => input.click());
      input.addEventListener('change', (e) => {
        handleFiles(which, e.target.files);
        e.target.value = '';
      });
    });
  }

  // ---------- STEP 5: INFO ----------
  function bindInfoInputs() {
    document.querySelectorAll('[data-vr-info]').forEach((input) => {
      input.addEventListener('input', (e) => {
        const key = e.target.dataset.vrInfo;
        state.info[key] = e.target.value;
        renderFooter();
      });
    });
  }

  // ---------- DROP ZONES ----------
  // Bind body video drop zone once (static markup). Inspo drops are bound
  // dynamically inside renderInspoStep via wireDrop.
  function bindDropZones() {
    document.querySelectorAll('[data-vr-drop="body"]').forEach((dz) => {
      const input = document.querySelector('[data-vr-drop-input="body"]');
      if (input) wireDrop(dz, input, 'body');
    });
  }

  function wireDrop(dz, input, which) {
    dz.addEventListener('click', () => input.click());
    dz.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); input.click(); }
    });
    dz.addEventListener('dragover', (e) => { e.preventDefault(); dz.classList.add('is-drag'); });
    dz.addEventListener('dragleave', () => dz.classList.remove('is-drag'));
    dz.addEventListener('drop', (e) => {
      e.preventDefault();
      dz.classList.remove('is-drag');
      handleFiles(which, e.dataTransfer.files);
    });
    input.addEventListener('change', (e) => {
      handleFiles(which, e.target.files);
      e.target.value = '';
    });
  }

  function handleFiles(which, fileList) {
    const files = Array.from(fileList || []);
    if (which === 'body') {
      if (files[0]) setBodyVideo(files[0]);
    } else {
      // inspo item key
      files.forEach((file) => addInspoFile(which, file));
    }
    renderFiles(which);
    renderFooter();
  }

  function addInspoFile(itemKey, file) {
    const bucket = ensureInspoBucket(itemKey);
    const isImage = /^image\/(png|jpeg|heic|heif)$/.test(file.type) || /\.(heic|heif)$/i.test(file.name);
    const tooBig = file.size > MAX_IMAGE_BYTES;
    const rec = {
      id: newId(),
      file,
      name: file.name,
      size: file.size,
      previewUrl: isImage ? URL.createObjectURL(file) : null,
      progress: 0,
      error: !isImage ? 'PNG, JPG, or HEIC only' : (tooBig ? 'Max 10MB' : null)
    };
    bucket.files.push(rec);
    if (!rec.error) simulateProgress(rec, () => renderFiles(itemKey));
  }

  function setBodyVideo(file) {
    const isVideo = /^video\/(mp4|quicktime)$/.test(file.type);
    const tooBig = file.size > MAX_VIDEO_BYTES;
    if (state.bodyVideo && state.bodyVideo.previewUrl) URL.revokeObjectURL(state.bodyVideo.previewUrl);
    const rec = {
      id: newId(),
      file,
      name: file.name,
      size: file.size,
      previewUrl: isVideo ? URL.createObjectURL(file) : null,
      progress: 0,
      error: !isVideo ? 'MP4 or MOV only' : (tooBig ? 'Max 10MB' : null)
    };
    state.bodyVideo = rec;
    if (!rec.error) simulateProgress(rec, () => renderFiles('body'));
  }

  function simulateProgress(rec, onUpdate) {
    const startedAt = performance.now();
    const dur = 700;
    function tick(now) {
      const t = Math.min(1, (now - startedAt) / dur);
      rec.progress = Math.round(t * 100);
      onUpdate();
      if (t < 1) requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }

  function removeInspoFile(itemKey, id) {
    const bucket = state.inspo[itemKey];
    if (!bucket) return;
    const i = bucket.files.findIndex((f) => f.id === id);
    if (i === -1) return;
    const f = bucket.files[i];
    if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
    bucket.files.splice(i, 1);
    renderFiles(itemKey);
    renderReview();
    renderFooter();
  }
  function removeBodyVideo() {
    if (state.bodyVideo && state.bodyVideo.previewUrl) URL.revokeObjectURL(state.bodyVideo.previewUrl);
    state.bodyVideo = null;
    renderFiles('body');
    renderReview();
    renderFooter();
  }

  // ---------- RENDER ----------
  function render() {
    renderSteps();
    renderProgress();
    renderInspoStep();
    renderFiles('body');
    renderFitStep();
    renderReview();
    renderFooter();
  }

  function renderSteps() {
    document.querySelectorAll('[data-vr-step]').forEach((el) => {
      const v = el.dataset.vrStep;
      const isActive = (v === String(state.step)) || (state.step === 'success' && v === 'success');
      if (isActive) el.removeAttribute('hidden');
      else el.setAttribute('hidden', '');
    });
  }

  function renderProgress() {
    const counter = document.querySelector('[data-vr-counter]');
    const label = document.querySelector('[data-vr-step-label]');
    const bar = document.querySelector('[data-vr-progress-bar]');
    const star = document.querySelector('[data-vr-progress-star]');

    const labels = { 1: 'Your Pick', 2: 'Your Inspo', 3: 'Your Fit', 4: 'Your Body', 5: 'Your Info', 6: 'Review' };
    const isSuccess = state.step === 'success';
    const steps = visibleSteps();
    const total = steps.length;
    const pos = isSuccess ? total : (steps.indexOf(state.step) + 1);
    const pct = (pos / total) * 100;

    if (counter) counter.textContent = pos + '/' + total;
    if (label) label.textContent = isSuccess ? 'Review' : labels[state.step];
    if (bar) bar.style.width = pct + '%';
    if (star) star.style.left = pct + '%';
  }

  function renderFiles(which) {
    const list = document.querySelector('[data-vr-files="' + which + '"]');
    if (!list) return;
    list.innerHTML = '';
    const isBody = which === 'body';
    const files = isBody
      ? (state.bodyVideo ? [state.bodyVideo] : [])
      : ((state.inspo[which] && state.inspo[which].files) || []);
    files.forEach((f) => {
      const li = document.createElement('li');
      li.className = 'vr-file' + (f.progress >= 100 && !f.error ? ' is-done' : '') + (f.error ? ' is-error' : '');
      const isHeic = /\.(heic|heif)$/i.test(f.name);
      const thumb = !isBody && f.previewUrl && !isHeic
        ? '<img class="vr-file__thumb" src="' + f.previewUrl + '" alt="">'
        : '<div class="vr-file__thumb' + (isHeic ? ' vr-file__thumb--heic' : '') + '">' + (isHeic ? 'HEIC' : '') + '</div>';
      li.innerHTML = thumb +
        '<div class="vr-file__meta">' +
          '<div class="vr-file__name">' + escapeHtml(f.name) + '</div>' +
          '<div class="vr-file__size">' + (f.error ? f.error : formatBytes(f.size)) + '</div>' +
        '</div>' +
        '<button type="button" class="vr-file__remove" aria-label="Remove">×</button>' +
        '<div class="vr-file__progress"><div class="vr-file__progress-bar" style="width:' + f.progress + '%"></div></div>' +
        '<span class="vr-file__progress-pct">' + f.progress + '%</span>' +
        '<svg class="vr-file__check" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="M5 12l5 5 9-11"/></svg>';
      li.querySelector('.vr-file__remove').addEventListener('click', () => {
        if (isBody) removeBodyVideo(); else removeInspoFile(which, f.id);
      });
      list.appendChild(li);
    });
  }

  // ---------- STEP 2: INSPO TABS + PANELS ----------
  function renderInspoStep() {
    const tabsEl = document.querySelector('[data-vr-inspo-tabs]');
    const panelsEl = document.querySelector('[data-vr-inspo-panels]');
    if (!tabsEl || !panelsEl) return;

    const items = inspoItems();
    tabsEl.innerHTML = '';
    panelsEl.innerHTML = '';

    if (items.length === 0) {
      panelsEl.innerHTML = '<p class="vr-sub">Go back and pick a category first.</p>';
      return;
    }

    const showTabs = items.length > 1;
    tabsEl.toggleAttribute('hidden', !showTabs);

    items.forEach((item, i) => {
      ensureInspoBucket(item.key);
      if (showTabs) {
        const tab = document.createElement('button');
        tab.type = 'button';
        tab.className = 'vr-fit-tab';
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
        tab.dataset.target = item.key;
        tab.textContent = item.label;
        tab.addEventListener('click', () => activateInspoTab(item.key));
        tabsEl.appendChild(tab);
      }

      const panel = document.createElement('div');
      panel.className = 'vr-inspo-panel';
      panel.dataset.inspoPanel = item.key;
      if (showTabs && i !== 0) panel.setAttribute('hidden', '');

      // Accessory type select (only for accessory items)
      if (item.cat === 'accessories') {
        const typeWrap = document.createElement('div');
        typeWrap.className = 'vr-field';
        const typeLabel = document.createElement('span');
        typeLabel.className = 'vr-field__label';
        typeLabel.innerHTML = 'Accessory Type <span aria-hidden="true">*</span>';
        typeWrap.appendChild(typeLabel);

        const otherWrap = document.createElement('label');
        otherWrap.className = 'vr-field';
        otherWrap.hidden = state.inspo[item.key].accessoryType !== 'Other';
        const otherLabel = document.createElement('span');
        otherLabel.className = 'vr-field__label';
        otherLabel.innerHTML = 'Tell us what kind <span aria-hidden="true">*</span>';
        const otherInput = document.createElement('input');
        otherInput.type = 'text';
        otherInput.value = state.inspo[item.key].accessoryTypeOther || '';
        otherInput.addEventListener('input', (e) => {
          state.inspo[item.key].accessoryTypeOther = e.target.value;
          renderFooter();
          renderReview();
        });
        otherWrap.appendChild(otherLabel);
        otherWrap.appendChild(otherInput);

        const select = createCustomSelect({
          options: ACCESSORY_TYPES,
          value: state.inspo[item.key].accessoryType || '',
          placeholder: 'Select Type',
          onChange: (v) => {
            state.inspo[item.key].accessoryType = v;
            otherWrap.hidden = v !== 'Other';
            if (v !== 'Other') state.inspo[item.key].accessoryTypeOther = '';
            renderFooter();
            renderReview();
          }
        });
        typeWrap.appendChild(select);
        panel.appendChild(typeWrap);
        panel.appendChild(otherWrap);
      }

      // text field
      const field = document.createElement('label');
      field.className = 'vr-field';
      const labelSpan = document.createElement('span');
      labelSpan.className = 'vr-field__label';
      labelSpan.innerHTML = 'Tell us what you\u2019re hunting for <span aria-hidden="true">*</span>';
      const input = document.createElement('input');
      input.type = 'text';
      input.placeholder = INSPO_PLACEHOLDERS[item.cat] || INSPO_PLACEHOLDER_DEFAULT;
      input.autocomplete = 'off';
      input.value = state.inspo[item.key].text || '';
      input.addEventListener('input', (e) => { state.inspo[item.key].text = e.target.value; renderFooter(); renderReview(); });
      field.appendChild(labelSpan);
      field.appendChild(input);
      panel.appendChild(field);

      // Optional, repeatable Pinterest links
      const bucket = state.inspo[item.key];
      if (!Array.isArray(bucket.pinterest)) bucket.pinterest = [];
      const linksWrap = document.createElement('div');
      linksWrap.className = 'vr-field';
      const linksLabel = document.createElement('span');
      linksLabel.className = 'vr-field__label';
      linksLabel.textContent = 'Pinterest links (optional)';
      linksWrap.appendChild(linksLabel);
      const linksList = document.createElement('div');
      linksList.className = 'vr-links';
      linksWrap.appendChild(linksList);
      const addLink = document.createElement('button');
      addLink.type = 'button';
      addLink.className = 'vr-link-add';
      addLink.textContent = '+ Add Pinterest link';
      linksWrap.appendChild(addLink);
      panel.appendChild(linksWrap);

      const drawLinks = (focusIdx) => {
        linksList.innerHTML = '';
        bucket.pinterest.forEach((val, idx) => {
          const rowEl = document.createElement('div');
          rowEl.className = 'vr-link-row';
          const linkInput = document.createElement('input');
          linkInput.type = 'url';
          linkInput.inputMode = 'url';
          linkInput.placeholder = 'https://pinterest.com/...';
          linkInput.autocomplete = 'off';
          linkInput.value = val;
          linkInput.addEventListener('input', (e) => { bucket.pinterest[idx] = e.target.value; renderReview(); });
          const removeLink = document.createElement('button');
          removeLink.type = 'button';
          removeLink.className = 'vr-link-remove';
          removeLink.setAttribute('aria-label', 'Remove link');
          removeLink.textContent = '×';
          removeLink.addEventListener('click', () => { bucket.pinterest.splice(idx, 1); drawLinks(); renderReview(); });
          rowEl.appendChild(linkInput);
          rowEl.appendChild(removeLink);
          linksList.appendChild(rowEl);
          if (focusIdx === idx) linkInput.focus();
        });
      };
      addLink.addEventListener('click', () => {
        bucket.pinterest.push('');
        drawLinks(bucket.pinterest.length - 1);
      });
      drawLinks();

      // drop zone
      const dropLabel = document.createElement('span');
      dropLabel.className = 'vr-field__label vr-drop-label';
      dropLabel.textContent = 'Upload images (optional)';
      panel.appendChild(dropLabel);

      const drop = document.createElement('div');
      drop.className = 'vr-drop';
      drop.tabIndex = 0;
      drop.setAttribute('role', 'button');
      drop.setAttribute('aria-label', 'Upload images for ' + item.label);
      drop.innerHTML =
        '<svg class="vr-drop__icon" viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" stroke-width="1.5">' +
          '<path d="M12 16V4M6 10l6-6 6 6"/>' +
          '<path d="M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3"/>' +
        '</svg>' +
        '<p>Drop your image here or click to <strong>browse</strong></p>' +
        '<p class="vr-drop__meta">PNG, JPG, HEIC (max 10MB)</p>';
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = 'image/png,image/jpeg,image/heic,image/heif';
      fileInput.multiple = true;
      fileInput.hidden = true;
      drop.appendChild(fileInput);
      panel.appendChild(drop);

      const fileList = document.createElement('ul');
      fileList.className = 'vr-files';
      fileList.dataset.vrFiles = item.key;
      panel.appendChild(fileList);

      panelsEl.appendChild(panel);

      wireDrop(drop, fileInput, item.key);
      renderFiles(item.key);
    });
  }

  function activateInspoTab(target) {
    document.querySelectorAll('[data-vr-inspo-tabs] .vr-fit-tab').forEach((t) => {
      t.setAttribute('aria-selected', t.dataset.target === target ? 'true' : 'false');
    });
    document.querySelectorAll('[data-inspo-panel]').forEach((p) => {
      if (p.dataset.inspoPanel === target) p.removeAttribute('hidden');
      else p.setAttribute('hidden', '');
    });
  }

  // Accessory items that need measurements (Belt type selected in step 2)
  function accessoryMeasurementItems() {
    return inspoItems().filter((i) => {
      if (i.cat !== 'accessories') return false;
      const t = state.inspo[i.key] && state.inspo[i.key].accessoryType;
      return !!ACCESSORY_MEASUREMENTS[t];
    });
  }

  // For an accessory item, return a label like "Belt" (single) or "Belt #2" (when
  // multiple items share that type). Use this everywhere we display an accessory item.
  function accessoryItemLabel(item) {
    const bucket = state.inspo[item.key] || {};
    const type = bucket.accessoryType;
    if (!type) return item.label;
    let typeLabel = type;
    if (type === 'Other' && bucket.accessoryTypeOther) typeLabel = 'Other — ' + bucket.accessoryTypeOther;
    const sameType = inspoItems().filter((i) => {
      if (i.cat !== 'accessories') return false;
      const t = state.inspo[i.key] && state.inspo[i.key].accessoryType;
      return t === type;
    });
    if (sameType.length <= 1) return typeLabel;
    const idx = sameType.findIndex((i) => i.key === item.key);
    return typeLabel + ' #' + (idx + 1);
  }

  function categoryNeedsFitStep(cat) {
    if (cat === 'accessories') return accessoryMeasurementItems().length > 0;
    return FIT_CONFIG[cat] && FIT_CONFIG[cat].fields.length > 0;
  }

  // Extra (non-measurement) fields shown on apparel panels, in display order.
  function apparelExtraFields(cat) {
    const cfg = FIT_CONFIG[cat];
    if (!cfg) return [];
    const out = [];
    if (cfg.fitPreference) out.push({ key: 'fitPreference', label: 'Fit preference' });
    if (cfg.height) out.push({ key: 'height', label: 'Height' });
    if (cfg.usualSize) out.push({ key: 'usualSize', label: cfg.usualSize.label || 'Usual size' });
    if (cfg.cup) {
      out.push({ key: 'bandSize', label: 'Band size' });
      out.push({ key: 'cupSize', label: 'Cup size' });
    }
    return out;
  }

  // Where a garment's fit info came from — for the review screen and Brittany's email.
  function fitSource(cat) {
    const cfg = FIT_CONFIG[cat];
    if (!cfg || !cfg.usualSize) return '';
    const b = state.fit[cat] || {};
    const hasSize = !!(b.usualSize && String(b.usualSize).trim());
    const hasMeas = cfg.fields.some((f) => b[f.key] && String(b[f.key]).trim());
    if (hasSize && hasMeas) return 'Measurements + usual size';
    if (hasMeas) return 'Measurements';
    if (hasSize) return 'Usual size';
    return '';
  }

  function fitTextField(labelText, value, opts) {
    const wrap = document.createElement('label');
    wrap.className = 'vr-field';
    const span = document.createElement('span');
    span.className = 'vr-field__label';
    span.innerHTML = escapeHtml(labelText) + (opts.required ? ' <span aria-hidden="true">*</span>' : '');
    const inp = document.createElement('input');
    inp.type = 'text';
    if (opts.placeholder) inp.placeholder = opts.placeholder;
    inp.value = value || '';
    if (opts.height) {
      wireHeightInput(inp, opts.onInput);
    } else {
      if (opts.numeric) wireNumericInput(inp);
      inp.addEventListener('input', (e) => opts.onInput(e.target.value));
    }
    wrap.appendChild(span);
    wrap.appendChild(inp);
    return wrap;
  }

  // Height input: first digit is feet, then auto-insert an apostrophe, then inches.
  // e.g. typing 5 → "5'", then 7 → "5'7". Digits only; caps at feet + 2 inch digits.
  function wireHeightInput(input, onInput) {
    input.inputMode = 'numeric';
    input.autocomplete = 'off';
    input.addEventListener('input', (e) => {
      const raw = e.target.value;
      const isDelete = e.inputType && e.inputType.indexOf('delete') !== -1;
      const keepSep = !isDelete || /'$/.test(raw); // add the ' while typing, or keep it on delete
      const digits = raw.replace(/\D/g, '').slice(0, 3);
      let out = '';
      if (digits.length >= 1) {
        out = digits[0];
        if (digits.length >= 2) out += "'" + digits.slice(1);
        else if (keepSep) out += "'";
      }
      e.target.value = out;
      onInput(out);
    });
  }

  function fitSelectField(labelText, value, options, required, onChange) {
    const wrap = document.createElement('div');
    wrap.className = 'vr-field';
    const span = document.createElement('span');
    span.className = 'vr-field__label';
    span.innerHTML = escapeHtml(labelText) + (required ? ' <span aria-hidden="true">*</span>' : '');
    const select = createCustomSelect({ options: options, value: value || '', placeholder: 'Select', onChange: onChange });
    wrap.appendChild(span);
    wrap.appendChild(select);
    return wrap;
  }

  // Builds the apparel-only top of a fit panel: required Height + Fit preference,
  // an optional "Your usual size" group, then the "Measurements" heading. The
  // caller appends the measurement inputs (cfg.fields) after this.
  function renderApparelTop(panel, cat, cfg) {
    const bucket = state.fit[cat] || (state.fit[cat] = {});

    panel.appendChild(fitSelectField('Fit preference', bucket.fitPreference, FIT_PREF_OPTIONS, true,
      (v) => { bucket.fitPreference = v; renderFooter(); renderReview(); }));

    panel.insertAdjacentHTML('beforeend',
      '<p class="vr-sub" style="margin:.25rem 0 1rem;font-weight:600;">Choose one to fill out — your usual size or your measurements, or both if you like.</p>' +
      '<h3 class="vr-fit-panel__heading">YOUR USUAL SIZE</h3>' +
      '<hr class="vr-fit-panel__rule">');

    const us = cfg.usualSize;
    if (us.type === 'select') {
      panel.appendChild(fitSelectField(us.label, bucket.usualSize, us.options, false,
        (v) => { bucket.usualSize = v; renderFooter(); renderReview(); }));
    } else {
      panel.appendChild(fitTextField(us.label, bucket.usualSize, {
        placeholder: us.placeholder, numeric: !!us.numeric, required: false,
        onInput: (v) => { bucket.usualSize = v; renderFooter(); renderReview(); }
      }));
    }
    if (cfg.cup) {
      panel.appendChild(fitSelectField('Band size', bucket.bandSize, BAND_SIZES, false,
        (v) => { bucket.bandSize = v; renderReview(); }));
      panel.appendChild(fitSelectField('Cup size', bucket.cupSize, CUP_SIZES, false,
        (v) => { bucket.cupSize = v; renderReview(); }));
    }

    panel.insertAdjacentHTML('beforeend',
      '<h3 class="vr-fit-panel__heading">MEASUREMENTS <span class="vr-fit-panel__heading-note">(in inches)</span></h3>' +
      '<hr class="vr-fit-panel__rule">');
  }

  // Apparel categories present in the fit step. Height is shared across all of them.
  function apparelCatsInFit() {
    return state.categories.filter((c) => categoryNeedsFitStep(c) && FIT_CONFIG[c] && FIT_CONFIG[c].usualSize);
  }
  function currentSharedHeight() {
    const cats = apparelCatsInFit();
    for (let i = 0; i < cats.length; i++) {
      const b = state.fit[cats[i]];
      if (b && b.height) return b.height;
    }
    return '';
  }
  function setSharedHeight(v) {
    apparelCatsInFit().forEach((c) => {
      if (!state.fit[c]) state.fit[c] = {};
      state.fit[c].height = v;
    });
  }

  function renderFitStep() {
    const tabsEl = document.querySelector('[data-vr-fit-tabs]');
    const panelsEl = document.querySelector('[data-vr-fit-panels]');
    if (!tabsEl || !panelsEl) return;

    const existingShared = document.querySelector('[data-vr-shared-height]');
    if (existingShared) existingShared.remove();

    const cats = state.categories.filter(categoryNeedsFitStep);
    tabsEl.innerHTML = '';
    panelsEl.innerHTML = '';

    if (cats.length === 0) {
      panelsEl.innerHTML = '<p class="vr-sub">No measurements needed for what you\u2019ve picked.</p>';
      return;
    }

    // Height is a body measurement \u2014 ask once, above the tabs, shared across all apparel.
    if (apparelCatsInFit().length > 0) {
      const h = currentSharedHeight();
      setSharedHeight(h); // propagate to any newly added apparel categories
      const heightField = fitTextField('Height', h, {
        placeholder: 'e.g. 5\'7"', height: true, required: true,
        onInput: (v) => { setSharedHeight(v); renderFooter(); renderReview(); }
      });
      heightField.setAttribute('data-vr-shared-height', '');
      tabsEl.parentNode.insertBefore(heightField, tabsEl);
    }

    const showTabs = cats.length > 1;
    tabsEl.toggleAttribute('hidden', !showTabs);

    cats.forEach((cat, i) => {
      const cfg = FIT_CONFIG[cat];
      if (showTabs) {
        const tab = document.createElement('button');
        tab.type = 'button';
        tab.className = 'vr-fit-tab';
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
        tab.dataset.target = cat;
        tab.textContent = cfg.label;
        tab.addEventListener('click', () => activateFitTab(cat));
        tabsEl.appendChild(tab);
      }
      const panel = document.createElement('div');
      panel.className = 'vr-fit-panel';
      panel.dataset.fitPanel = cat;
      if (showTabs && i !== 0) panel.setAttribute('hidden', '');
      const isApparel = !!cfg.usualSize;
      if (isApparel) {
        // Apparel panels build their own section headings in renderApparelTop.
        panel.innerHTML = '';
      } else {
        const headingSuffix = cat === 'shoes' ? '' : ' <span class="vr-fit-panel__heading-note">(in inches)</span>';
        panel.innerHTML =
          '<h3 class="vr-fit-panel__heading">MEASUREMENTS' + headingSuffix + '</h3>' +
          '<hr class="vr-fit-panel__rule">';
      }

      // Accessories: per-item conditional measurements (type comes from step 2)
      if (cat === 'accessories') {
        const items = accessoryMeasurementItems();
        items.forEach((item) => {
          const type = state.inspo[item.key].accessoryType;
          const fields = ACCESSORY_MEASUREMENTS[type] || [];
          if (!state.fit[item.key]) state.fit[item.key] = {};
          if (items.length > 1) {
            const subHead = document.createElement('div');
            subHead.className = 'vr-review__category';
            subHead.style.margin = '1rem 0 0.5rem';
            subHead.textContent = accessoryItemLabel(item);
            panel.appendChild(subHead);
          }
          fields.forEach((subField) => {
            const sw = document.createElement('label');
            sw.className = 'vr-field';
            const lbl = document.createElement('span');
            lbl.className = 'vr-field__label';
            lbl.textContent = subField.label;
            const inp = document.createElement('input');
            inp.type = 'text';
            if (subField.placeholder) inp.placeholder = subField.placeholder;
            inp.value = state.fit[item.key][subField.key] || '';
            wireNumericInput(inp);
            inp.addEventListener('input', (e) => {
              state.fit[item.key][subField.key] = e.target.value;
              renderReview();
            });
            sw.appendChild(lbl);
            sw.appendChild(inp);
            panel.appendChild(sw);
          });
        });
        panelsEl.appendChild(panel);
        return;
      }

      // ensure state bucket
      if (!state.fit[cat]) state.fit[cat] = {};
      // Apparel: Height + Fit preference + optional Usual size, above the measurements group.
      if (isApparel) renderApparelTop(panel, cat, cfg);
      cfg.fields.forEach((field) => {
        let otherWrap = null;
        const condWraps = []; // { value, wrap, input, subKey }

        const wrap = document.createElement((field.type === 'select' || field.type === 'radio') ? 'div' : 'label');
        wrap.className = 'vr-field';
        const span = document.createElement('span');
        span.className = 'vr-field__label';
        if (field.required) span.innerHTML = escapeHtml(field.label) + ' <span aria-hidden="true">*</span>';
        else span.textContent = field.label;
        wrap.appendChild(span);

        const onValueChange = (v) => {
          state.fit[cat][field.key] = v;
          if (field.otherWhen && otherWrap) {
            otherWrap.hidden = v !== field.otherWhen;
          }
          if (field.conditionalFields) {
            // Clear values for non-matching conditional groups, then toggle.
            Object.keys(field.conditionalFields).forEach((condValue) => {
              if (condValue !== v) {
                field.conditionalFields[condValue].forEach((sf) => {
                  delete state.fit[cat][sf.key];
                });
              }
            });
            condWraps.forEach((cw) => {
              const visible = cw.value === v;
              cw.wrap.hidden = !visible;
              if (!visible && cw.input) cw.input.value = '';
            });
          }
          renderFooter();
          renderReview();
        };

        if (field.type === 'radio') {
          const group = document.createElement('div');
          group.className = 'vr-radio-group';
          (field.options || []).forEach((opt) => {
            const optLabel = document.createElement('label');
            optLabel.className = 'vr-radio';
            const radio = document.createElement('input');
            radio.type = 'radio';
            radio.name = cat + '_' + field.key;
            radio.value = opt;
            radio.checked = state.fit[cat][field.key] === opt;
            radio.addEventListener('change', () => onValueChange(opt));
            const optText = document.createElement('span');
            optText.textContent = opt;
            optLabel.appendChild(radio);
            optLabel.appendChild(optText);
            group.appendChild(optLabel);
          });
          wrap.appendChild(group);
        } else if (field.type === 'select') {
          const select = createCustomSelect({
            options: field.options || [],
            value: state.fit[cat][field.key] || '',
            placeholder: field.placeholder || 'Select',
            onChange: onValueChange
          });
          wrap.appendChild(select);
        } else {
          const control = document.createElement('input');
          control.type = 'text';
          if (field.placeholder) control.placeholder = field.placeholder;
          control.value = state.fit[cat][field.key] || '';
          wireNumericInput(control);
          control.addEventListener('input', (e) => onValueChange(e.target.value));
          wrap.appendChild(control);
        }
        panel.appendChild(wrap);

        if (field.otherWhen && field.otherKey) {
          otherWrap = document.createElement('label');
          otherWrap.className = 'vr-field';
          otherWrap.hidden = state.fit[cat][field.key] !== field.otherWhen;
          const otherLabel = document.createElement('span');
          otherLabel.className = 'vr-field__label';
          otherLabel.textContent = field.otherLabel || 'Please specify';
          const otherInput = document.createElement('input');
          otherInput.type = 'text';
          otherInput.value = state.fit[cat][field.otherKey] || '';
          otherInput.addEventListener('input', (e) => {
            state.fit[cat][field.otherKey] = e.target.value;
            renderReview();
          });
          otherWrap.appendChild(otherLabel);
          otherWrap.appendChild(otherInput);
          panel.appendChild(otherWrap);
        }

        if (field.conditionalFields) {
          const currentVal = state.fit[cat][field.key];
          Object.keys(field.conditionalFields).forEach((condValue) => {
            field.conditionalFields[condValue].forEach((subField) => {
              const sw = document.createElement('label');
              sw.className = 'vr-field';
              sw.hidden = currentVal !== condValue;
              const lbl = document.createElement('span');
              lbl.className = 'vr-field__label';
              lbl.textContent = subField.label;
              const inp = document.createElement('input');
              inp.type = 'text';
              if (subField.placeholder) inp.placeholder = subField.placeholder;
              inp.value = state.fit[cat][subField.key] || '';
              wireNumericInput(inp);
              inp.addEventListener('input', (e) => {
                state.fit[cat][subField.key] = e.target.value;
                renderReview();
              });
              sw.appendChild(lbl);
              sw.appendChild(inp);
              panel.appendChild(sw);
              condWraps.push({ value: condValue, wrap: sw, input: inp, subKey: subField.key });
            });
          });
        }
      });
      panelsEl.appendChild(panel);
    });
  }

  function activateFitTab(target) {
    document.querySelectorAll('[data-vr-fit-tabs] .vr-fit-tab').forEach((t) => {
      t.setAttribute('aria-selected', t.dataset.target === target ? 'true' : 'false');
    });
    document.querySelectorAll('[data-fit-panel]').forEach((p) => {
      if (p.dataset.fitPanel === target) p.removeAttribute('hidden');
      else p.setAttribute('hidden', '');
    });
  }

  function renderReview() {
    const root = document.querySelector('[data-vr-review]');
    if (!root) return;
    root.innerHTML = '';

    // YOUR FIT — per-category tabs (mirrors step 3 input UX)
    const fitCol = document.createElement('div');
    fitCol.className = 'vr-review__section';
    fitCol.innerHTML = '<h4>YOUR FIT</h4><hr>';
    const fitCats = state.categories.filter(categoryNeedsFitStep);
    if (fitCats.length === 0) {
      fitCol.innerHTML += '<p class="vr-review__row"><dd>—</dd></p>';
    } else {
      if (!state.reviewFitTab || fitCats.indexOf(state.reviewFitTab) === -1) {
        state.reviewFitTab = fitCats[0];
      }
      const activeCat = state.reviewFitTab;

      if (fitCats.length > 1) {
        const tabsEl = document.createElement('div');
        tabsEl.className = 'vr-fit-tabs';
        tabsEl.setAttribute('role', 'tablist');
        fitCats.forEach((cat) => {
          const cfg = FIT_CONFIG[cat];
          const tab = document.createElement('button');
          tab.type = 'button';
          tab.className = 'vr-fit-tab';
          tab.setAttribute('role', 'tab');
          tab.setAttribute('aria-selected', cat === activeCat ? 'true' : 'false');
          tab.textContent = cfg.label;
          tab.addEventListener('click', () => {
            state.reviewFitTab = cat;
            renderReview();
          });
          tabsEl.appendChild(tab);
        });
        fitCol.appendChild(tabsEl);
      }

      if (activeCat === 'accessories') {
        // Per-item display: type + measurements
        const items = accessoryMeasurementItems();
        let hasAny = false;
        items.forEach((item) => {
          const type = state.inspo[item.key].accessoryType;
          const fields = ACCESSORY_MEASUREMENTS[type] || [];
          const bucket = state.fit[item.key] || {};
          const head = document.createElement('div');
          head.className = 'vr-review__category';
          head.textContent = accessoryItemLabel(item);
          fitCol.appendChild(head);
          const dl = document.createElement('dl');
          fields.forEach((sf) => {
            const sv = bucket[sf.key];
            if (!sv) return;
            hasAny = true;
            const row = document.createElement('div');
            row.className = 'vr-review__row';
            row.innerHTML = '<dt>' + escapeHtml(sf.label) + ':</dt><dd>' + escapeHtml(sv) + '</dd>';
            dl.appendChild(row);
          });
          fitCol.appendChild(dl);
        });
        if (!hasAny) fitCol.innerHTML += '<p class="vr-review__row"><dd>—</dd></p>';
      } else {
        const cfg = FIT_CONFIG[activeCat];
        const bucket = state.fit[activeCat] || {};
        const rows = [];
        // Apparel extras (fit preference, height, usual size, cup) first...
        apparelExtraFields(activeCat).forEach(({ key, label }) => {
          const v = bucket[key];
          if (v && String(v).trim()) rows.push([label, v]);
        });
        // ...then measurements.
        cfg.fields.forEach((field) => {
          const v = bucket[field.key];
          if (!v) return;
          let display = v;
          if (field.otherWhen && v === field.otherWhen && bucket[field.otherKey]) {
            display = 'Other — ' + bucket[field.otherKey];
          }
          rows.push([field.label, display]);
        });
        const source = fitSource(activeCat);
        if (source) {
          const badge = document.createElement('div');
          badge.className = 'vr-review__category';
          badge.textContent = 'From: ' + source;
          fitCol.appendChild(badge);
        }
        if (rows.length) {
          const dl = document.createElement('dl');
          rows.forEach(([label, val]) => {
            const row = document.createElement('div');
            row.className = 'vr-review__row';
            row.innerHTML = '<dt>' + escapeHtml(label) + ':</dt><dd>' + escapeHtml(val) + '</dd>';
            dl.appendChild(row);
          });
          fitCol.appendChild(dl);
        } else if (!source) {
          fitCol.innerHTML += '<p class="vr-review__row"><dd>—</dd></p>';
        }
      }
    }
    root.appendChild(fitCol);

    // Your Body — hidden when step 4 is skipped (accessories-only)
    if (visibleSteps().indexOf(4) !== -1) {
      const bodyCol = document.createElement('div');
      bodyCol.className = 'vr-review__section';
      bodyCol.innerHTML = '<h4>YOUR BODY</h4><hr>';
      if (state.bodyVideo && !state.bodyVideo.error) {
        const wrap = document.createElement('div');
        wrap.className = 'vr-review__file';
        wrap.innerHTML = '<div class="vr-file__thumb"></div><span>' + escapeHtml(state.bodyVideo.name) + '</span>' +
          '<button type="button" class="vr-file__remove" aria-label="Remove video">×</button>';
        wrap.querySelector('.vr-file__remove').addEventListener('click', removeBodyVideo);
        bodyCol.appendChild(wrap);
      } else {
        bodyCol.innerHTML += '<p class="vr-review__row"><dd>—</dd></p>';
      }
      root.appendChild(bodyCol);
    }

    // Your Inspo (per item)
    const inspoCol = document.createElement('div');
    inspoCol.className = 'vr-review__section';
    inspoCol.innerHTML = '<h4>YOUR INSPO</h4><hr>';
    const items = inspoItems();
    let anyInspo = false;
    items.forEach((item) => {
      const bucket = state.inspo[item.key];
      if (!bucket) return;
      const validFiles = bucket.files.filter((f) => !f.error);
      const links = (bucket.pinterest || []).map((s) => String(s).trim()).filter(Boolean);
      const accType = bucket.accessoryType;
      if (!bucket.text && validFiles.length === 0 && !accType && links.length === 0) return;
      anyInspo = true;
      const block = document.createElement('div');
      const head = document.createElement('div');
      head.className = 'vr-review__category';
      let label = item.label;
      if (item.cat === 'accessories' && accType) {
        label = accessoryItemLabel(item);
      }
      head.textContent = label;
      block.appendChild(head);
      if (bucket.text) {
        const p = document.createElement('p');
        p.textContent = '"' + bucket.text + '"';
        p.style.color = '#676766';
        p.style.margin = '0 0 0.5rem';
        p.style.fontSize = '0.9rem';
        block.appendChild(p);
      }
      links.forEach((url) => {
        const a = document.createElement('a');
        a.href = url;
        a.textContent = url;
        a.target = '_blank';
        a.rel = 'noopener';
        a.style.display = 'block';
        a.style.margin = '0 0 0.35rem';
        a.style.fontSize = '0.85rem';
        a.style.color = 'var(--vr-wine)';
        a.style.wordBreak = 'break-all';
        block.appendChild(a);
      });
      validFiles.forEach((f) => {
        const wrap = document.createElement('div');
        wrap.className = 'vr-review__file';
        const isHeicRev = /\.(heic|heif)$/i.test(f.name);
        const img = f.previewUrl && !isHeicRev
          ? '<img src="' + f.previewUrl + '" alt="">'
          : '<div class="vr-file__thumb' + (isHeicRev ? ' vr-file__thumb--heic' : '') + '">' + (isHeicRev ? 'HEIC' : '') + '</div>';
        wrap.innerHTML = img + '<span>' + escapeHtml(f.name) + '</span>' +
          '<button type="button" class="vr-file__remove" aria-label="Remove image">×</button>';
        wrap.querySelector('.vr-file__remove').addEventListener('click', () => removeInspoFile(item.key, f.id));
        block.appendChild(wrap);
      });
      inspoCol.appendChild(block);
    });
    if (!anyInspo) inspoCol.innerHTML += '<p class="vr-review__row"><dd>—</dd></p>';
    root.appendChild(inspoCol);

    // Your Info
    const infoCol = document.createElement('div');
    infoCol.className = 'vr-review__section';
    infoCol.innerHTML = '<h4>YOUR INFO</h4><hr>';
    const lines = [state.info.name, state.info.email, state.info.phone, state.info.instagram].filter(Boolean);
    if (lines.length === 0) infoCol.innerHTML += '<p>—</p>';
    else lines.forEach((line) => {
      const p = document.createElement('p');
      p.textContent = line;
      p.style.margin = '0 0 0.4rem';
      p.style.color = '#676766';
      p.style.fontSize = '0.9rem';
      p.style.lineHeight = '1.5';
      infoCol.appendChild(p);
    });
    root.appendChild(infoCol);
  }

  // ---------- NAV / VALIDATION ----------
  function bindNav(form) {
    document.querySelector('[data-vr-back]').addEventListener('click', goBack);
    document.querySelector('[data-vr-skip]').addEventListener('click', goNext);
    document.querySelector('[data-vr-next]').addEventListener('click', goNext);
    document.querySelector('[data-vr-submit]').addEventListener('click', (e) => { e.preventDefault(); submit(); });
    const restart = document.querySelector('[data-vr-restart]');
    if (restart) restart.addEventListener('click', resetForm);
    if (form) form.addEventListener('submit', (e) => { e.preventDefault(); submit(); });
  }

  function renderFooter() {
    const back = document.querySelector('[data-vr-back]');
    const skip = document.querySelector('[data-vr-skip]');
    const next = document.querySelector('[data-vr-next]');
    const submit = document.querySelector('[data-vr-submit]');

    const isSuccess = state.step === 'success';
    if (isSuccess) {
      back.setAttribute('hidden', ''); skip.setAttribute('hidden', '');
      next.setAttribute('hidden', ''); submit.setAttribute('hidden', '');
      return;
    }

    const steps = visibleSteps();
    const lastStep = steps[steps.length - 1];

    // Back: visible on any step past the first visible
    if (state.step !== steps[0]) back.removeAttribute('hidden'); else back.setAttribute('hidden', '');

    // Step 4 (optional body video): show Skip until a video is added, then Next.
    const onVideoStep = state.step === 4;
    const hasVideo = !!(state.bodyVideo && !state.bodyVideo.error);
    skip.toggleAttribute('hidden', !(onVideoStep && !hasVideo));

    // Next vs Submit
    if (state.step === lastStep) {
      next.setAttribute('hidden', '');
      submit.removeAttribute('hidden');
    } else if (onVideoStep && !hasVideo) {
      // Skip stands in for Next until a video is uploaded.
      next.setAttribute('hidden', '');
      submit.setAttribute('hidden', '');
    } else {
      next.removeAttribute('hidden');
      submit.setAttribute('hidden', '');
    }

    // Next disabled state
    next.disabled = !canAdvance();
    submit.disabled = !canSubmit();
  }

  function canAdvance() {
    if (state.step === 1) return state.categories.length > 0;
    if (state.step === 2) return validateInspo();
    if (state.step === 3) return validateFit();
    if (state.step === 5) return validateInfo();
    return true;
  }

  // Each item needs a "what you're hunting for" description; accessory items also
  // need a type (and the "what kind" detail when the type is Other).
  function validateInspo() {
    const items = inspoItems();
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const b = state.inspo[item.key] || {};
      if (!(b.text && b.text.trim())) return false;
      if (item.cat === 'accessories') {
        if (!b.accessoryType) return false;
        if (b.accessoryType === 'Other' && !(b.accessoryTypeOther && b.accessoryTypeOther.trim())) return false;
      }
    }
    return true;
  }

  // Each apparel garment needs a fit preference, a height, and either a usual
  // size or at least one measurement. Shoes/accessories stay optional.
  function validateFit() {
    const cats = state.categories.filter(categoryNeedsFitStep);
    for (let i = 0; i < cats.length; i++) {
      const cfg = FIT_CONFIG[cats[i]];
      if (!cfg) continue;
      const b = state.fit[cats[i]] || {};
      // Apparel: fit preference + height + (a usual size or a measurement).
      if (cfg.usualSize) {
        if (!b.fitPreference) return false;
        if (!(b.height && String(b.height).trim())) return false;
        const hasSize = !!(b.usualSize && String(b.usualSize).trim());
        const hasMeas = cfg.fields.some((f) => b[f.key] && String(b[f.key]).trim());
        if (!hasSize && !hasMeas) return false;
      }
      // Any fields explicitly marked required (e.g. shoes: gender + size).
      for (let j = 0; j < cfg.fields.length; j++) {
        const f = cfg.fields[j];
        if (f.required && !(b[f.key] && String(b[f.key]).trim())) return false;
      }
    }
    return true;
  }
  function canSubmit() {
    return state.categories.length > 0 && validateInfo();
  }

  function validateInfo() {
    const { name, email, phone } = state.info;
    if (!name.trim() || !email.trim() || !phone.trim()) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  function goBack() {
    if (state.step === 'success') return;
    const steps = visibleSteps();
    const i = steps.indexOf(state.step);
    if (i > 0) state.step = steps[i - 1];
    render();
    scrollTop();
  }
  function goNext() {
    if (state.step === 'success') return;
    if (!canAdvance()) return;
    const steps = visibleSteps();
    const i = steps.indexOf(state.step);
    if (i >= 0 && i < steps.length - 1) state.step = steps[i + 1];
    render();
    scrollTop();
  }
  function scrollTop() {
    const panel = document.querySelector('.vr-modal__panel');
    if (panel) panel.scrollTop = 0;
  }

  // Reset everything back to a blank step 1 (used by "Start new request").
  function resetForm() {
    if (state.bodyVideo && state.bodyVideo.previewUrl) URL.revokeObjectURL(state.bodyVideo.previewUrl);
    Object.keys(state.inspo).forEach((k) => {
      (state.inspo[k].files || []).forEach((f) => { if (f.previewUrl) URL.revokeObjectURL(f.previewUrl); });
    });
    state.step = 1;
    state.categories = [];
    state.quantities = {};
    state.inspo = {};
    state.fit = {};
    state.bodyVideo = null;
    state.info = { name: '', email: '', phone: '', instagram: '' };
    state.reviewFitTab = null;
    document.querySelectorAll('[data-vr-category]').forEach((tile) => {
      tile.setAttribute('aria-checked', 'false');
      setTileCount(tile, 1);
    });
    document.querySelectorAll('[data-vr-info]').forEach((inp) => { inp.value = ''; });
    clearSubmitError();
    render();
    scrollTop();
  }

  // Serialize state.fit for the email: apparel buckets get a canonical key order
  // and a __source tag (Measurements / Usual size / both). Shoes and per-item
  // accessory buckets pass through untouched.
  function buildFitPayload() {
    const out = {};
    Object.keys(state.fit).forEach((key) => {
      const bucket = state.fit[key] || {};
      const cfg = FIT_CONFIG[key];
      if (!cfg || !cfg.usualSize) { out[key] = bucket; return; }
      const ordered = {};
      const source = fitSource(key);
      if (source) ordered.__source = source;
      if (bucket.fitPreference) ordered.fitPreference = bucket.fitPreference;
      if (bucket.height) ordered.height = bucket.height;
      if (bucket.usualSize) ordered.usualSize = bucket.usualSize;
      if (bucket.bandSize) ordered.bandSize = bucket.bandSize;
      if (bucket.cupSize) ordered.cupSize = bucket.cupSize;
      cfg.fields.forEach((f) => { if (bucket[f.key]) ordered[f.key] = bucket[f.key]; });
      out[key] = ordered;
    });
    return out;
  }

  // ---------- SUBMIT ----------
  function submit() {
    if (!canSubmit()) return;
    clearSubmitError();
    const submitBtn = document.querySelector('[data-vr-submit]');
    submitBtn.disabled = true;
    submitBtn.textContent = 'Sending...';

    const fd = new FormData();
    fd.append('categories', JSON.stringify(state.categories));
    fd.append('quantities', JSON.stringify(state.quantities));
    const activeItems = inspoItems();
    const inspoText = {};
    const accessoryTypes = {};
    const pinterestLinks = {};
    activeItems.forEach((item) => {
      const b = state.inspo[item.key];
      if (!b) return;
      if (b.text) inspoText[item.key] = b.text;
      if (item.cat === 'accessories' && b.accessoryType) {
        accessoryTypes[item.key] = {
          type: b.accessoryType,
          typeOther: b.accessoryTypeOther || ''
        };
      }
      const links = (b.pinterest || []).map((s) => String(s).trim()).filter(Boolean);
      if (links.length) pinterestLinks[item.key] = links;
    });
    fd.append('inspoText', JSON.stringify(inspoText));
    fd.append('accessoryTypes', JSON.stringify(accessoryTypes));
    fd.append('pinterestLinks', JSON.stringify(pinterestLinks));
    fd.append('fit', JSON.stringify(buildFitPayload()));
    fd.append('info', JSON.stringify(state.info));
    activeItems.forEach((item) => {
      const b = state.inspo[item.key];
      if (!b) return;
      b.files.filter((f) => !f.error).forEach((f) => {
        fd.append('inspoFiles[' + item.key + '][]', f.file, f.name);
      });
    });
    if (state.bodyVideo && !state.bodyVideo.error) fd.append('bodyVideo', state.bodyVideo.file, state.bodyVideo.name);

    const doneAndShowSuccess = () => {
      state.step = 'success';
      render();
      submitBtn.textContent = 'Send my request';
    };

    if (!SUBMIT_ENDPOINT) {
      // No backend yet — log to console for debugging and simulate success.
      const inspoSummary = {};
      inspoItems().forEach((item) => {
        const b = state.inspo[item.key];
        if (!b) return;
        inspoSummary[item.key] = {
          text: b.text,
          files: b.files.map((f) => ({ name: f.name, size: f.size }))
        };
      });
      console.info('[vintage-request] submission (stub):', {
        categories: state.categories,
        quantities: state.quantities,
        inspo: inspoSummary,
        fit: state.fit,
        info: state.info,
        bodyVideo: state.bodyVideo ? { name: state.bodyVideo.name, size: state.bodyVideo.size } : null
      });
      setTimeout(doneAndShowSuccess, 700);
      return;
    }

    fetch(SUBMIT_ENDPOINT, { method: 'POST', body: fd })
      .then(async (r) => {
        if (!r.ok) {
          let msg = '';
          try { const body = await r.json(); if (body && body.error) msg = body.error; } catch (e) {}
          throw new Error(msg || 'Something went wrong sending your request. Please try again.');
        }
        return r.json().catch(() => ({}));
      })
      .then(doneAndShowSuccess)
      .catch((err) => {
        console.error(err);
        const networkish = !err || err.message === 'Failed to fetch' || err.name === 'TypeError';
        showSubmitError(networkish
          ? 'Couldn’t reach the server. Check your connection and try again.'
          : err.message);
        submitBtn.disabled = false;
        submitBtn.textContent = 'Send my request';
      });
  }

  // Inline, styled submit error (replaces the native alert box).
  function showSubmitError(msg) {
    let el = document.querySelector('[data-vr-error]');
    if (!el) {
      const footer = document.querySelector('[data-vr-footer]');
      el = document.createElement('p');
      el.className = 'vr-error';
      el.setAttribute('data-vr-error', '');
      el.setAttribute('role', 'alert');
      if (footer && footer.parentNode) footer.parentNode.insertBefore(el, footer);
    }
    el.textContent = msg;
    el.hidden = false;
    el.scrollIntoView({ block: 'nearest' });
  }
  function clearSubmitError() {
    const el = document.querySelector('[data-vr-error]');
    if (el) el.hidden = true;
  }

  // ---------- UTIL ----------
  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
  }
  function formatBytes(n) {
    if (n < 1024) return n + ' B';
    if (n < 1024 * 1024) return (n / 1024).toFixed(0) + ' KB';
    return (n / (1024 * 1024)).toFixed(1) + ' MB';
  }
})();
